import React, {ReactNode, useCallback, useState, useEffect, useRef} from "react"
import {
	Box,
	Button,
	TextField,
	Typography,
	FormControlLabel,
	Checkbox,
	CircularProgress,
	Alert,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Chip,
	Menu,
	MenuItem,
	Skeleton,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloseIcon from "@mui/icons-material/Close"

import {useDraftReply} from "@indebted/api/conversationDraftReplyAPI"
import {useSendReply} from "@indebted/api/conversationSendReplyAPI"
import {api} from "@indebted/api"
import type {DraftReplyBlocked, DraftReplyDraft, EmailRecipient} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type DraftReplyPanelProps = {
	customerID: string
	conversationID: string
	providerMessageId: string
	repliesToEarlierMessage?: boolean
	onSent?: () => void
}

type Result =
	| {kind: "ok"; draft: DraftReplyDraft; omittedFields: string[]; contactWarning?: string}
	| {kind: "blocked"; reason: DraftReplyBlocked["reason"]}

// The sent state carries the document that actually went, because the picker
// stays live afterwards and a later pick must not rewrite what was confirmed.
type SendState = {kind: "idle"} | {kind: "sent"; attachment: PickableDocument | null} | {kind: "error"}

type PickableDocument = {
	noteId: string
	uploadId: string
	fileName: string
}

type DocumentsState = {kind: "loading"} | {kind: "ready"; documents: PickableDocument[]} | {kind: "failed"}

const FACT_FIELDS = [
	{label: "Name", field: "customer_name"},
	{label: "Balance", field: "outstanding_balance"},
	{label: "Last payment amount", field: "last_payment_amount"},
	{label: "Last payment date", field: "last_payment_date"},
	{label: "Original creditor", field: "original_creditor"},
	{label: "Payment link", field: "payment_portal_link"},
]

function DraftReplyPanel({
	customerID,
	conversationID,
	providerMessageId,
	repliesToEarlierMessage,
	onSent,
}: DraftReplyPanelProps) {
	const {notification} = useNotification()
	const {mutateAsync, isPending} = useDraftReply()
	const {mutateAsync: sendReply, isPending: isSending} = useSendReply()
	const [agentEmail, agentName] = useAgent()

	const [guidance, setGuidance] = useState("")
	const [fields, setFields] = useState<string[]>([])
	const [replyInCustomerLanguage, setReplyInCustomerLanguage] = useState(true)
	const [result, setResult] = useState<Result | null>(null)
	const [body, setBody] = useState("")
	const [confirmingDiscard, setConfirmingDiscard] = useState(false)
	const [sendState, setSendState] = useState<SendState>({kind: "idle"})
	const [documentsState, setDocumentsState] = useState<DocumentsState>({kind: "loading"})
	const [selectedDocument, setSelectedDocument] = useState<PickableDocument | null>(null)

	const mounted = useRef(true)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const loadDocuments = useCallback(() => {
		setDocumentsState({kind: "loading"})
		api.customer
			.getNotes(customerID)
			.then((response) => {
				if (!mounted.current) return
				setDocumentsState({kind: "ready", documents: noteUploads(response.Notes)})
			})
			.catch(() => {
				if (mounted.current) setDocumentsState({kind: "failed"})
			})
	}, [customerID])

	useEffect(() => {
		loadDocuments()
	}, [loadDocuments])

	function toggleField(field: string, checked: boolean) {
		setFields((current) => (checked ? [...current, field] : current.filter((f) => f !== field)))
	}

	async function generateDraft() {
		setSendState({kind: "idle"})
		try {
			const response = await mutateAsync({
				customerID,
				conversationID,
				providerMessageId,
				guidance,
				fields,
				replyInCustomerLanguage,
			})
			if (response.status === "ok") {
				setResult({
					kind: "ok",
					draft: response.draft,
					omittedFields: response.omittedFields ?? [],
					contactWarning: response.contactWarning,
				})
				setBody(response.draft.bodyText.trim())
			} else {
				setResult({kind: "blocked", reason: response.reason})
			}
		} catch {
			notification.error("Failed to generate the draft reply")
		}
	}

	function handleGenerateDraft() {
		// A sent reply's edits are no longer unsaved work.
		if (sendState.kind !== "sent" && result?.kind === "ok" && hasBodyChanged(body, result.draft.bodyText)) {
			setConfirmingDiscard(true)
			return
		}
		void generateDraft()
	}

	function discardEditsAndGenerate() {
		setConfirmingDiscard(false)
		void generateDraft()
	}

	async function handleApproveAndSend() {
		if (result?.kind !== "ok") return
		const edited = hasBodyChanged(body, result.draft.bodyText)
		try {
			const response = await sendReply({
				customerID,
				conversationID,
				providerMessageId,
				bodyHtml: result.draft.bodyHtml,
				// Unedited posts the draft's own text, trailing separator and all, so
				// what the customer receives stays byte-identical to today.
				bodyText: edited ? body : result.draft.bodyText,
				edited,
				guidance,
				authoredBy: agentEmail ?? "",
				modelVersion: result.draft.modelVersion,
				...(selectedDocument
					? {attachment: {noteId: selectedDocument.noteId, uploadId: selectedDocument.uploadId}}
					: {}),
			})
			if (!mounted.current) return
			// A 2xx that is not an acceptance comes from a handler older than this
			// build; reporting it as sent would claim a reply nothing recorded.
			if (response.status !== "accepted") {
				setSendState({kind: "error"})
				return
			}
			setSendState({kind: "sent", attachment: selectedDocument})
			onSent?.()
		} catch {
			if (mounted.current) setSendState({kind: "error"})
		}
	}

	return (
		<PanelColumn>
			<Typography variant="subtitle2" color="textPrimary">
				Draft a reply
			</Typography>
			{repliesToEarlierMessage && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					Replying to an earlier message — the customer has sent newer messages since.
				</Alert>
			)}
			<TextField
				label="Instructions"
				placeholder="Tell the assistant how to reply"
				value={guidance}
				onChange={(event) => setGuidance(event.target.value)}
				multiline
				minRows={3}
				fullWidth
				size="small"
			/>

			<FieldSet label="Include these customer details in the reply">
				<Box display="flex" gap={2} flexWrap="wrap">
					{FACT_FIELDS.map(({label, field}) => (
						<FormControlLabel
							key={field}
							control={
								<Checkbox
									checked={fields.includes(field)}
									onChange={(event) => toggleField(field, event.target.checked)}
								/>
							}
							label={label}
						/>
					))}
				</Box>
			</FieldSet>

			<FieldSet label="Reply language">
				<FormControlLabel
					control={
						<Checkbox
							checked={replyInCustomerLanguage}
							onChange={(event) => setReplyInCustomerLanguage(event.target.checked)}
						/>
					}
					label="Reply in the customer's language"
				/>
			</FieldSet>

			<AttachDocuments
				state={documentsState}
				selected={selectedDocument}
				onSelect={setSelectedDocument}
				onRemove={() => setSelectedDocument(null)}
				onRetry={loadDocuments}
			/>

			<Button
				variant="contained"
				color="primary"
				aria-label="Generate draft"
				disabled={isPending}
				onClick={handleGenerateDraft}
				sx={{alignSelf: "flex-start"}}
			>
				{isPending ? <CircularProgress size={24} /> : "Generate draft"}
			</Button>
			<DiscardEditsDialog
				open={confirmingDiscard}
				onCancel={() => setConfirmingDiscard(false)}
				onConfirm={discardEditsAndGenerate}
			/>
			{result && <Divider />}
			{result?.kind === "ok" &&
				(sendState.kind === "sent" ? (
					<Alert severity="success" sx={{borderRadius: 2}}>
						Reply sent by {agentName ?? agentEmail}
						{sendState.attachment ? ` with ${sendState.attachment.fileName}` : ""}
					</Alert>
				) : (
					<DraftPreview
						draft={result.draft}
						body={body}
						onBodyChange={setBody}
						omittedFields={result.omittedFields}
						contactWarning={result.contactWarning}
						onApproveAndSend={handleApproveAndSend}
						isSending={isSending}
						canSend={Boolean(agentEmail)}
						sendState={sendState}
					/>
				))}
			{result?.kind === "blocked" && <BlockedBanner reason={result.reason} />}
		</PanelColumn>
	)
}

// Trim the same way the seeding does, or the field and this comparison disagree
// about the baseline.
function hasBodyChanged(body: string, draftBodyText: string) {
	return body.trim() !== draftBodyText.trim()
}

function isBlank(body: string) {
	return body.trim() === ""
}

function DiscardEditsDialog({open, onCancel, onConfirm}: {open: boolean; onCancel: () => void; onConfirm: () => void}) {
	return (
		<Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
			<DialogTitle>Discard your edits?</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary">
					Generating another draft replaces the reply body, and the changes you have made to it will be lost.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} size="small">
					Keep editing
				</Button>
				<Button variant="contained" size="small" onClick={onConfirm}>
					Discard &amp; generate
				</Button>
			</DialogActions>
		</Dialog>
	)
}

function PanelColumn({children}: {children: ReactNode}) {
	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{children}
		</Box>
	)
}

function FieldSet({label, children}: {label: string; children: ReactNode}) {
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			border="1px solid"
			borderColor="grey.200"
			borderRadius={1}
			p={1.5}
		>
			<Typography variant="caption" color="textSecondary" textTransform="uppercase" letterSpacing={0.5}>
				{label}
			</Typography>
			{children}
		</Box>
	)
}

// noteUploads keeps only active notes, matching the notes surface where an
// archived note's upload is not downloadable.
function noteUploads(
	notes: Array<{ID: string; Status: {ID: string}; Uploads: Array<{ID: string; FileName: string}>}>,
): PickableDocument[] {
	return notes
		.filter((note) => note.Status.ID === "active")
		.flatMap((note) =>
			(note.Uploads ?? []).map((upload) => ({noteId: note.ID, uploadId: upload.ID, fileName: upload.FileName})),
		)
}

function AttachDocuments({
	state,
	selected,
	onSelect,
	onRemove,
	onRetry,
}: {
	state: DocumentsState
	selected: PickableDocument | null
	onSelect: (document: PickableDocument) => void
	onRemove: () => void
	onRetry: () => void
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null)

	if (state.kind === "loading") {
		return (
			<FieldSet label="Attach a document to the sent reply">
				<Skeleton variant="rounded" width={150} height={32} />
			</FieldSet>
		)
	}

	if (state.kind === "failed") {
		return (
			<FieldSet label="Attach a document to the sent reply">
				<Alert
					severity="warning"
					sx={{borderRadius: 2}}
					action={
						<Button size="small" onClick={onRetry}>
							Retry
						</Button>
					}
				>
					{"Couldn't load this customer's documents."}
				</Alert>
			</FieldSet>
		)
	}

	return (
		<FieldSet label="Attach a document to the sent reply">
			{state.documents.length === 0 ? (
				<Typography variant="caption" color="textSecondary">
					No documents on file for this customer.
				</Typography>
			) : selected ? (
				<Chip
					label={selected.fileName}
					title={selected.fileName}
					size="small"
					icon={<AttachFileIcon />}
					deleteIcon={<CloseIcon titleAccess={`Remove ${selected.fileName}`} />}
					onDelete={onRemove}
					sx={{alignSelf: "flex-start"}}
				/>
			) : (
				<>
					<Button
						variant="outlined"
						size="small"
						startIcon={<AttachFileIcon />}
						onClick={(event) => setAnchor(event.currentTarget)}
						sx={{alignSelf: "flex-start"}}
					>
						Attach document
					</Button>
					<Menu
						anchorEl={anchor}
						open={Boolean(anchor)}
						onClose={() => setAnchor(null)}
						slotProps={{paper: {sx: {maxWidth: 360}}}}
					>
						{state.documents.map((document) => (
							<MenuItem
								key={document.uploadId}
								title={document.fileName}
								sx={{display: "block", overflow: "hidden", textOverflow: "ellipsis"}}
								onClick={() => {
									onSelect(document)
									setAnchor(null)
								}}
							>
								{document.fileName}
							</MenuItem>
						))}
					</Menu>
				</>
			)}
		</FieldSet>
	)
}

function DraftPreview({
	draft,
	body,
	onBodyChange,
	omittedFields,
	contactWarning,
	onApproveAndSend,
	isSending,
	canSend,
	sendState,
}: {
	draft: DraftReplyDraft
	body: string
	onBodyChange: (body: string) => void
	omittedFields: string[]
	contactWarning?: string
	onApproveAndSend: () => void
	isSending: boolean
	canSend: boolean
	sendState: SendState
}) {
	const omittedLabels = omittedFields
		.map((field) => FACT_FIELDS.find((fact) => fact.field === field)?.label)
		.filter((label): label is string => Boolean(label))

	return (
		<PanelColumn>
			{contactWarning && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					This customer has an active {contactWarning} case.
				</Alert>
			)}
			<Box component="section" aria-label="Drafted email" display="flex" flexDirection="column" gap={1}>
				<Typography variant="subtitle2" color="textPrimary">
					Drafted email
				</Typography>
				<RecipientRow label="From" recipient={draft.from} />
				<RecipientRow label="To" recipient={draft.to} />
				<Typography variant="body2" color="textPrimary" fontWeight={600}>
					{draft.subject}
				</Typography>
				<TextField
					label="Reply body"
					value={body}
					onChange={(event) => onBodyChange(event.target.value)}
					multiline
					minRows={8}
					fullWidth
					size="small"
				/>
				{draft.footerText && (
					<>
						<Divider sx={{mt: 1}} />
						<Typography variant="caption" color="textSecondary" sx={{whiteSpace: "pre-wrap"}}>
							{draft.footerText}
						</Typography>
					</>
				)}
			</Box>

			{omittedLabels.length > 0 && (
				<Alert severity="info" sx={{borderRadius: 2}}>
					Not included — no data on file: {omittedLabels.join(", ")}
				</Alert>
			)}

			{sendState.kind === "error" && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't send the reply. Try again."}
				</Alert>
			)}

			<Button
				variant="contained"
				color="primary"
				aria-label="Approve & send"
				disabled={!canSend || isSending || isBlank(body)}
				onClick={onApproveAndSend}
				sx={{alignSelf: "flex-start"}}
			>
				{isSending ? <CircularProgress size={24} /> : "Approve & send"}
			</Button>
			{!canSend && (
				<Typography variant="caption" color="textSecondary">
					Sign in to send replies.
				</Typography>
			)}
		</PanelColumn>
	)
}

function RecipientRow({label, recipient}: {label: string; recipient: EmailRecipient}) {
	return (
		<Typography variant="caption" color="textSecondary">
			{label}: {recipient.label ? `${recipient.label} <${recipient.email}>` : recipient.email}
		</Typography>
	)
}

function BlockedBanner({reason}: {reason: DraftReplyBlocked["reason"]}) {
	// A reason this build does not know is one the API gained or has not yet
	// dropped; naming the wrong cause is worse than naming none.
	const message =
		reason === "prompt_injection"
			? "This reply was blocked for a suspected prompt injection in the customer message."
			: reason === "conduct_exhausted"
				? "The assistant couldn't write a reply that passed compliance review. Add instructions and generate again."
				: "This reply was blocked. Try again, and escalate if it keeps happening."

	return (
		<Alert severity="warning" sx={{borderRadius: 2}}>
			{message}
		</Alert>
	)
}

export {DraftReplyPanel}
